<template>
  <div class="tier-fact-first-container">
    <FiltersWrapper>
      <CollapseBlock label="Фильтры" :alwaysHideExpand="true">
        <div class="filters-content">
          <CustomSelect
            label="Жилой комплекс"
            :value="this.filters.resident_id"
            @change="onResidentChange"
            :options="this.residents"
            optionValueKey="resident_id"
            optionLabelKey="resident_name"
            :loading="residentsFetching"
          />
          <CustomSelect
            label="Блок"
            :value="this.filters.block_id"
            @change="onBlockChange"
            :options="this.blocks"
            optionValueKey="block_id"
            optionLabelKey="block_name"
            :loading="blocksFetching"
            :disable="!this.filters.resident_id"
          />
          <CustomSelect
            label="Конструктив"
            :value="this.filters.work_group_id"
            @change="onWorkGroupChange"
            :options="this.workGroups"
            optionValueKey="work_group_id"
            optionLabelKey="work_group_name"
            :loading="workGroupsFetching"
            :disable="!this.filters.block_id"
          />
        </div>
      </CollapseBlock>
    </FiltersWrapper>
    
    <div class="table-container tier-fact-first-table">
      <div v-if="!this.filters.work_group_id" class="no-data">
        Выберите конструктив для отображения данных
      </div>
      <div v-else>
        <div class="table-header-with-nav">
          <q-btn
            flat
            dense
            round
            icon="chevron_left"
            size="md"
            @click="goBackward"
            class="nav-btn"
            title="Предыдущий период"
          />
          <div class="table-title-section">
            <span class="table-title">План-Факт</span>
            <span class="table-period" v-if="currentPeriodLabel">{{ currentPeriodLabel }}</span>
          </div>
          <q-btn
            flat
            dense
            round
            icon="chevron_right"
            size="md"
            @click="goForward"
            class="nav-btn"
            title="Следующий период"
          />
          <q-btn
            unelevated
            color="primary"
            label="Tier 2-6"
            size="md"
            @click="goToTier2to6"
            class="tier-2-6-btn"
            title="Перейти к Tier 2-6"
          />
          <q-btn
            flat
            dense
            round
            icon="refresh"
            size="md"
            @click="refreshTableData"
            class="refresh-btn"
            :loading="isRefreshing"
            title="Обновить данные"
          />
          <q-btn
            flat
            dense
            round
            icon="autorenew"
            size="md"
            @click="recalcData"
            class="recalc-btn"
            :loading="isRecalcing"
            title="Пересчитать витрину"
            color="primary"
          />
          <q-btn
            flat
            dense
            round
            :icon="showCalculationDetails ? 'visibility' : 'visibility_off'"
            size="md"
            @click="showCalculationDetails = !showCalculationDetails"
            class="details-btn"
            :class="{ 'details-btn-active': showCalculationDetails }"
            :title="showCalculationDetails ? 'Скрыть комментарии расчёта' : 'Показать комментарии расчёта'"
          />
        </div>
        <div class="table-wrapper" ref="tableWrapper">
          <CustomTable
            :rowsData="this.matrix"
            :columns="this.columns"
            :loading="isFetching"
            :small="true"
            :striped="false"
            :customBody="true"
            :customHeader="true"
            :allWidth="false"
          >
            <template #customHeader="props">
              <!-- Строка 1: Дни и Итого -->
              <q-tr>
                <q-th rowspan="2" class="sticky-column task-name-header">
                  <div>Задача</div>
                  <div v-if="selectedWorkGroup?.contractor_fio" class="contractor-info-cell">
                    {{ selectedWorkGroup.contractor_fio }}
                  </div>
                </q-th>
                <template v-for="(group, groupIdx) in getDateGroups()" :key="`group-${groupIdx}`">
                  <!-- Колонки дат в группе -->
                  <q-th 
                    v-for="dateInfo in group.dates" 
                    :key="dateInfo.colKey"
                    colspan="1"
                    class="date-header"
                    :class="{ 
                      'today-header': dateInfo.isToday,
                      'weekend-header': isWeekend(dateInfo.isoDate),
                      'collapsed-column': isCollapsedColumn(dateInfo),
                      'extra-day-column': !!dateInfo.is_extra_day
                    }"
                  >
                    <div class="date-header-content">
                      <div class="day-number" :class="{ 'date-changed': dateInfo.isoDate !== dateInfo.originalIsoDate }">{{ formatDateShort(dateInfo.isoDate) }}</div>
                      <div class="day-weekday">{{ getWeekday(dateInfo.isoDate) }}</div>
                    </div>
                  </q-th>
                  <!-- Колонки План/Факт после группы -->
                  <template v-if="shouldShowTotalsAfterGroup(groupIdx)">
                    <q-th 
                      colspan="1"
                      rowspan="2"
                      class="total-header"
                      :key="`total-plan-${groupIdx}`"
                    >
                      <div class="total-header-content">
                        <div class="total-label">План</div>
                      </div>
                    </q-th>
                    <q-th 
                      colspan="1"
                      rowspan="2"
                      class="total-header"
                      :key="`total-fact-${groupIdx}`"
                    >
                      <div class="total-header-content">
                        <div class="total-label">Факт</div>
                      </div>
                    </q-th>
                  </template>
                </template>
              </q-tr>
              <!-- Строка 2: П/Ф -->
              <q-tr>
                <template v-for="(group, groupIdx) in getDateGroups()" :key="`group-pf-${groupIdx}`">
                  <!-- Колонки П/Ф для дат в группе -->
                  <q-th 
                    v-for="dateInfo in group.dates" 
                    :key="`${dateInfo.colKey}-plan-fact`"
                    class="plan-fact-header"
                    :class="{
                      'collapsed-column': isCollapsedColumn(dateInfo),
                      'extra-day-column': !!dateInfo.is_extra_day
                    }"
                  >
                    <div class="plan-fact-header-content">
                      <span class="plan-label">П</span>
                      <span class="fact-label">Ф</span>
                    </div>
                  </q-th>
                </template>
              </q-tr>
            </template>

            <template #customBody="props">
              <!-- Специальная обработка для строки "Выход людей" -->
              <q-tr v-if="props.row.is_labor" :key="`labor-row-${props.row.task_name}`" class="labor-row">
                <q-td class="sticky-column task-name-cell labor-task-cell">
                  <div class="task-info">
                    <div class="task-name labor-task-name">{{ props.row.task_name }}</div>
                  </div>
                </q-td>
                <template v-for="(group, groupIdx) in getDateGroups()" :key="`group-body-labor-${groupIdx}`">
                  <q-td 
                    v-for="dateInfo in group.dates" 
                    :key="dateInfo.colKey"
                    class="data-cell labor-cell"
                    :class="{ 
                      'today-cell': dateInfo.isToday,
                      'has-data': props.row[dateInfo.colKey],
                      'collapsed-column': isCollapsedColumn(dateInfo),
                      'extra-day-column': !!dateInfo.is_extra_day
                    }"
                    @click="handleCellClick(props.row, dateInfo)"
                  >
                    <div v-if="props.row[dateInfo.colKey]" class="labor-cell-grid">
                      <!-- План (левый) -->
                      <div class="labor-grid-cell labor-plan-cell">
                        {{
                          props.row[dateInfo.colKey].plan_people === null ||
                          props.row[dateInfo.colKey].plan_people === undefined
                            ? '-'
                            : props.row[dateInfo.colKey].plan_people
                        }}
                      </div>
                      <!-- Факт (правый) -->
                      <div
                        class="labor-grid-cell labor-fact-cell"
                        :class="getLaborFactCellClass(
                          props.row[dateInfo.colKey].plan_people,
                          props.row[dateInfo.colKey].fact_people
                        )"
                      >
                        {{
                          props.row[dateInfo.colKey].fact_people === null ||
                          props.row[dateInfo.colKey].fact_people === undefined
                            ? '-'
                            : props.row[dateInfo.colKey].fact_people
                        }}
                      </div>
                    </div>
                    <div v-else class="labor-cell-grid">
                      <!-- Пустая ячейка -->
                      <div class="labor-grid-cell labor-plan-cell">-</div>
                      <div class="labor-grid-cell labor-fact-cell">-</div>
                    </div>
                  </q-td>
                  <template v-if="shouldShowTotalsAfterGroup(groupIdx)">
                    <q-td class="total-cell" :key="`total-plan-cell-labor-${groupIdx}`">
                      <div class="total-cell-content">-</div>
                    </q-td>
                    <q-td class="total-cell" :key="`total-fact-cell-labor-${groupIdx}`">
                      <div class="total-cell-content">-</div>
                    </q-td>
                  </template>
                </template>
              </q-tr>
              <!-- Строка этажности -->
              <q-tr v-else-if="props.row.task_name === 'Этажность'" :key="`floors-row-${props.row.task_name}`" class="floors-row">
                <q-td class="sticky-column task-name-cell floors-task-cell">
                  <div class="task-info">
                    <div class="task-name floors-task-name">{{ props.row.task_name }}</div>
                  </div>
                </q-td>
                <template v-for="(group, groupIdx) in getDateGroups()" :key="`group-body-floor-${groupIdx}`">
                  <q-td 
                    v-for="dateInfo in group.dates" 
                    :key="dateInfo.colKey"
                    class="data-cell floor-cell"
                    :class="{
                      'today-cell': dateInfo.isToday,
                      'collapsed-column': isCollapsedColumn(dateInfo),
                      'extra-day-column': !!dateInfo.is_extra_day
                    }"
                    :style="getFloorCellStyle(props.row[dateInfo.colKey], dateInfo)"
                    :title="props.row[dateInfo.colKey]?.calc_comment || ''"
                  >
                    <div class="floor-cell-content" :style="{ color: getFloorCellStyle(props.row[dateInfo.colKey], dateInfo).color }">
                      {{ getFloorDisplay(props.row[dateInfo.colKey]) }}
                    </div>
                  </q-td>
                  <template v-if="shouldShowTotalsAfterGroup(groupIdx)">
                    <q-td class="total-cell" :key="`total-plan-cell-floor-${groupIdx}`">
                      <div class="total-cell-content">-</div>
                    </q-td>
                    <q-td class="total-cell" :key="`total-fact-cell-floor-${groupIdx}`">
                      <div class="total-cell-content">-</div>
                    </q-td>
                  </template>
                </template>
              </q-tr>
              <!-- Обычные строки с задачами -->
              <q-tr v-else :key="props.row.task_dict_id || `task-${props.row.task_name}`">
                <q-td class="sticky-column task-name-cell">
                  <div class="task-info">
                    <div class="task-name">{{ props.row.task_name }} <span class="task-id-debug">[{{ props.row.task_dict_id }}]</span></div>
                    <div v-if="props.row.unit_name" class="unit-name">{{ props.row.unit_name }}</div>
                  </div>
                </q-td>
                <template v-for="(group, groupIdx) in getDateGroups()" :key="`group-body-${groupIdx}`">
                  <q-td 
                    v-for="dateInfo in group.dates" 
                    :key="dateInfo.colKey"
                    class="data-cell"
                    :class="{ 
                      'today-cell': dateInfo.isToday,
                      'weekend-cell': isWeekend(dateInfo.isoDate),
                      'has-data': props.row[dateInfo.colKey],
                      'auto-closed-cell': props.row[dateInfo.colKey]?.is_auto_closed,
                      'collapsed-column': isCollapsedColumn(dateInfo),
                      'extra-day-column': !!dateInfo.is_extra_day
                    }"
                    @click="handleCellClick(props.row, dateInfo)"
                  >
                    <div v-if="props.row[dateInfo.colKey]" class="cell-wrapper" :title="getCellTooltip(props.row[dateInfo.colKey])">
                      <div v-if="props.row[dateInfo.colKey].fact_value !== null && props.row[dateInfo.colKey].fact_value !== undefined" class="fact-info-row">
                        <span class="fact-entered">
                          <q-icon name="edit" size="10px" />
                          {{ formatValueOrDash(props.row[dateInfo.colKey].fact_value) }}
                        </span>
                        <span v-if="props.row[dateInfo.colKey].reserve_out_value > 0" class="reserve-value">
                          <q-icon name="trending_up" size="10px" />
                          {{ formatValueOrDash(props.row[dateInfo.colKey].reserve_out_value) }}
                        </span>
                      </div>
                      <div class="cell-grid-2x2">
                        <div class="grid-cell plan-cell top-left">
                          {{ formatValueOrDash(props.row[dateInfo.colKey].plan_value) }}
                        </div>
                        <div 
                          class="grid-cell fact-cell top-right"
                          :class="getPlanCloseClass(props.row[dateInfo.colKey])"
                        >
                          {{ formatValueOrDash(props.row[dateInfo.colKey].plan_closed_value) }}
                        </div>
                        <div 
                          class="grid-cell debt-cell bottom-left"
                          :class="{ 'has-debt': props.row[dateInfo.colKey].debt_in_value > 0 }"
                        >
                          {{ formatDebtValue(props.row[dateInfo.colKey].debt_in_value) }}
                        </div>
                        <div 
                          class="grid-cell debt-close-cell bottom-right"
                          :class="getDebtCloseClass(props.row[dateInfo.colKey])"
                        >
                          {{ formatDebtValue(props.row[dateInfo.colKey].debt_closed_value) }}
                        </div>
                      </div>
                    </div>
                    <div v-else class="cell-wrapper">
                      <div class="cell-grid-2x2">
                        <div class="grid-cell plan-cell top-left">-</div>
                        <div class="grid-cell fact-cell top-right">-</div>
                        <div class="grid-cell debt-cell bottom-left">-</div>
                        <div class="grid-cell debt-close-cell bottom-right">-</div>
                      </div>
                    </div>
                  </q-td>
                  <template v-if="shouldShowTotalsAfterGroup(groupIdx)">
                    <q-td class="total-cell total-plan-cell" :key="`total-plan-cell-${groupIdx}`">
                      <div class="total-cell-content">
                        {{ calculateRowPlanTotal(props.row, group.dates) }}
                      </div>
                    </q-td>
                    <q-td class="total-cell total-fact-cell" :key="`total-fact-cell-${groupIdx}`">
                      <div class="total-cell-content">
                        {{ calculateRowFactTotal(props.row, group.dates) }}
                      </div>
                    </q-td>
                  </template>
                </template>
              </q-tr>
            </template>
          </CustomTable>
        </div>
        
        <!-- Таблица причин отставания -->
        <div v-if="reasonsData.length > 0" class="reasons-table-container">
          <div class="reasons-table-title">Причины невыполнения плана на день</div>
          <div class="reasons-table-wrapper">
            <table class="reasons-table">
              <thead>
                <tr>
                  <th class="reasons-table-header-number">№</th>
                  <th class="reasons-table-header-reason">Причины невыполнения плана на день</th>
                  <th 
                    v-for="colNum in 20" 
                    :key="`reasons-col-${colNum}`"
                    class="reasons-table-header-date"
                  >
                    {{ colNum }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(reason, reasonIdx) in reasonsData" :key="reason.reason_id">
                  <td class="reasons-table-cell-number">{{ reasonIdx + 1 }}</td>
                  <td class="reasons-table-cell-reason">{{ reason.reason_name }}</td>
                  <td 
                    v-for="colNum in 20" 
                    :key="`reasons-cell-${reason.reason_id}-${colNum}`"
                    class="reasons-table-cell-date"
                  >
                    {{ getReasonDateForColumn(reason, colNum) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomSelect from "@/components/form/CustomSelect";
import CollapseBlock from "@/components/common/CollapseBlock";
import FiltersWrapper from "@/components/common/FiltersWrapper";
import CustomTable from "@/components/common/CustomTable";
import {
  getResidents,
  getBlocks,
  getWorkGroups,
  getTimelineData,
  getCurrentHalfMonth,
  getHalfMonthRangeISO,
  getHalfMonthFromDate,
  getNextHalfMonth,
  getPreviousHalfMonth,
  saveFact,
  deleteFact,
  saveLaborFact,
  getTimelineReasons,
} from "./services";
import { MODAL_NAMES_AIDAR } from "@/constants/index_aidar";
import { PAGES } from "@/constants";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

export default {
  name: "TierFactFirst",
  components: { CustomSelect, CollapseBlock, FiltersWrapper, CustomTable },
  setup() {
    const appStore = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    
    return {
      showModal: appStore.showModal,
      hideModal: appStore.closeModal,
      showSucceedSnackbar,
      showErrorSnackbar,
    };
  },
  data() {
    const initialHalfMonth = getCurrentHalfMonth();
    
    return {
      matrix: [],
      columns: [],
      dates: [],
      tasks: [],
      isFetching: false,
      isRefreshing: false,
      isRecalcing: false,
      residents: [],
      blocks: [],
      workGroups: [],
      residentsFetching: false,
      blocksFetching: false,
      workGroupsFetching: false,
      floorDistribution: {},
      reasonsData: [], // Данные по причинам из timeline__reasons_read
      showCalculationDetails: false, // Режим просмотра комментариев расчёта
      filters: {
        resident_id: '',
        block_id: '',
        work_group_id: '',
      },
      currentHalfMonth: {
        year: initialHalfMonth.year,
        month: initialHalfMonth.month,
        half: initialHalfMonth.half
      },
    };
  },
  computed: {
    selectedWorkGroup() {
      if (!this.filters.work_group_id) return null;
      return this.workGroups.find(wg => wg.work_group_id === this.filters.work_group_id) || null;
    },
    currentPeriodLabel() {
      const { year, month, half } = this.currentHalfMonth || {};
      if (!year || !month || !half) return '';

      const { date_start, date_end } = getHalfMonthRangeISO(year, month, half) || {};
      if (!date_start || !date_end) return '';

      const toDisplay = (isoDate) => {
        const y = isoDate.slice(0, 4);
        const m = isoDate.slice(5, 7);
        const d = isoDate.slice(8, 10);
        return `${d}.${m}.${y}`;
      };

      return `с ${toDisplay(date_start)} - ${toDisplay(date_end)}`;
    },
  },
  methods: {
    isCollapsedColumn(dateInfo) {
      // Схлопнутые логические дни: 2+ день на той же календарной дате
      return !!dateInfo && Number(dateInfo.ahead_days || 0) > 0;
    },
    formatValueOrDash(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      if (typeof value === 'number') {
        return Number.isInteger(value) ? value : value.toFixed(2);
      }
      return value;
    },
    
    getCellTooltip(cellData) {
      // Показываем tooltip только если включён режим просмотра комментариев
      if (!this.showCalculationDetails) return null;
      if (!cellData) return '';
      let tooltip = '';
      if (cellData.work_task_fact_id) {
        tooltip += 'work_task_fact_id: ' + cellData.work_task_fact_id;
      }
      if (cellData.calculation_details) {
        tooltip += (tooltip ? '\n\n' : '') + cellData.calculation_details;
      }
      return tooltip || null;
    },
    
    formatDebtValue(value) {
      if (value === null || value === undefined) {
        return '-';
      }
      if (typeof value === 'number') {
        return Number.isInteger(value) ? value : value.toFixed(2);
      }
      return value;
    },
    
    // Обновление URL параметров
    updateURLParams() {
      const query = {};
      
      if (this.filters.resident_id) {
        query.resident_id = this.filters.resident_id;
      }
      if (this.filters.block_id) {
        query.block_id = this.filters.block_id;
      }
      if (this.filters.work_group_id) {
        query.work_group_id = this.filters.work_group_id;
      }
      
      // Добавляем период
      query.year = this.currentHalfMonth.year;
      query.month = this.currentHalfMonth.month;
      query.half = this.currentHalfMonth.half;
      
      // Обновляем URL без перезагрузки страницы
      this.$router.replace({ query }).catch(() => {});
    },
    
    // Загрузка фильтров из URL
    async loadFiltersFromURL() {
      const { resident_id, block_id, work_group_id, year, month, half } = this.$route.query;

      if (year && month && half) {
        this.currentHalfMonth = {
          year: parseInt(year),
          month: parseInt(month),
          half: half
        };
      }

      if (resident_id) {
        this.filters.resident_id = parseInt(resident_id);
        await this.loadBlocks(this.filters.resident_id);

        if (block_id) {
          this.filters.block_id = parseInt(block_id);
          await this.loadWorkGroups(this.filters.block_id);

          if (work_group_id) {
            this.filters.work_group_id = parseInt(work_group_id);
            if (!year || !month || !half) {
              const wg = this.workGroups.find((w) => w.work_group_id === parseInt(work_group_id));
              const dateStart = wg?.date_start || wg?.date_start_iso;
              const halfFromPlan = dateStart ? getHalfMonthFromDate(dateStart) : null;
              if (halfFromPlan) {
                this.currentHalfMonth = { ...halfFromPlan };
              }
            }
            await this.loadTimelineData(this.filters.work_group_id);
          }
        }
      }
    },
    
    goToTier2to6() {
      const query = {};
      if (this.filters?.resident_id) {
        query.resident_id = this.filters.resident_id;
      }
      this.$router
        .push({ path: PAGES.tierFactTimelineTier2to6, query })
        .catch(() => {});
    },
    
    goBackward() {
      const prev = getPreviousHalfMonth(this.currentHalfMonth.year, this.currentHalfMonth.month, this.currentHalfMonth.half);
      this.currentHalfMonth = prev;
      this.updateURLParams();
      this.loadTimelineData(this.filters.work_group_id);
    },
    
    goForward() {
      const next = getNextHalfMonth(this.currentHalfMonth.year, this.currentHalfMonth.month, this.currentHalfMonth.half);
      this.currentHalfMonth = next;
      this.updateURLParams();
      this.loadTimelineData(this.filters.work_group_id);
    },
    
    async loadResidents() {
      this.residentsFetching = true;
      this.residents = await getResidents();
      this.residentsFetching = false;
    },
    
    async loadBlocks(resident_id) {
      this.blocksFetching = true;
      this.blocks = await getBlocks(resident_id);
      this.blocksFetching = false;
    },
    
    async loadWorkGroups(block_id) {
      this.workGroupsFetching = true;
      this.workGroups = await getWorkGroups(block_id);
      this.workGroupsFetching = false;
    },
    
    onResidentChange(val) {
      this.filters.resident_id = val;
      this.filters.block_id = '';
      this.filters.work_group_id = '';
      this.blocks = [];
      this.workGroups = [];
      this.matrix = [];
      this.dates = [];
      
      this.updateURLParams();
      
      if (val) {
        this.loadBlocks(val);
      }
    },
    
    onBlockChange(val) {
      this.filters.block_id = val;
      this.filters.work_group_id = '';
      this.workGroups = [];
      this.matrix = [];
      this.dates = [];
      
      this.updateURLParams();
      
      if (val) {
        this.loadWorkGroups(val);
      }
    },
    
    async onWorkGroupChange(val) {
      this.filters.work_group_id = val;
      this.matrix = [];
      this.dates = [];

      if (val) {
        const wg = this.workGroups.find((w) => w.work_group_id == val);
        const dateStart = wg?.date_start ?? wg?.date_start_iso;
        const halfFromPlan = dateStart ? getHalfMonthFromDate(dateStart) : null;
        if (halfFromPlan) {
          this.currentHalfMonth = { ...halfFromPlan };
        }
      }

      this.updateURLParams();

      if (val) {
        await this.loadTimelineData(val);
      }
    },
    
    async loadTimelineData(work_group_id) {
      if (!work_group_id) return;
      
      this.isFetching = true;
      try {
        const result = await getTimelineData(
          work_group_id,
          this.currentHalfMonth.year,
          this.currentHalfMonth.month,
          this.currentHalfMonth.half
        );
        this.tasks = result.tasks;
        this.matrix = result.matrix;
        this.dates = result.dates;
        
        let date_start = null, date_end = null;
        if (this.dates.length > 0) {
          const halfRange = getHalfMonthRangeISO(
            this.currentHalfMonth.year,
            this.currentHalfMonth.month,
            this.currentHalfMonth.half
          );
          date_start = halfRange.date_start;
          date_end = halfRange.date_end;
          const isoDates = this.dates.map((d) => d.isoDate).filter(Boolean);
          if (isoDates.length > 0) {
            const minDate = isoDates.reduce((a, b) => (a < b ? a : b));
            const maxDate = isoDates.reduce((a, b) => (a > b ? a : b));
            if (minDate < date_start) date_start = minDate;
            if (maxDate > date_end) date_end = maxDate;
          }
        }
        await this.loadReasons(work_group_id, date_start, date_end);
        this.buildReasonsFromMatrix();
        
        this.columns = this.generateColumns();
        
        if (this.dates.length > 0) {
          this.distributeFloorsByDates();
          this.addFloorsRow();
        }
      } catch (e) {
        console.error('Error loading timeline data:', e);
      } finally {
        this.isFetching = false;
      }
    },
    
    
    // Цепочка по причинам отставания:
    // Запись: ячейка факта → onSubmitFact → saveFact (reason_id, fact_date) → API tier_fact_first__fact_save
    //        → БД tier.tier_fact_first__fact_save пишет в work_task_fact_tab (reason_id, fact_date).
    // Чтение: loadReasons → getTimelineReasons → API timeline/reasons/read → БД tier.timeline__reasons_read
    //        должна вернуть по каждой причине строку дат (колонка dates), собранную из work_task_fact_tab по work_group_id и диапазону дат.
    async loadReasons(work_group_id, date_start = null, date_end = null) {
      try {
        if (!work_group_id) {
          this.reasonsData = [];
          return;
        }
        const raw = await getTimelineReasons(work_group_id, date_start, date_end);
        if (process.env.NODE_ENV !== 'production' && raw?.length) {
          console.log('[TierFactFirst] timeline reasons raw from API:', JSON.stringify(raw));
        }
        this.reasonsData = this.deduplicateReasonsByName(this.normalizeReasonsData(raw || []));
      } catch (e) {
        console.error('Error loading timeline reasons:', e);
        this.reasonsData = [];
      }
    },

    deduplicateReasonsByName(reasonsList) {
      const byName = {};
      (reasonsList || []).forEach((r) => {
        const name = (r.reason_name || '').trim();
        if (!name) return;
        const datesStr = (r.dates || '').trim();
        const datesArr = datesStr ? datesStr.split(',').map((d) => d.trim()).filter(Boolean) : [];
        if (!byName[name]) {
          byName[name] = { reason_id: r.reason_id, reason_name: name, datesSet: new Set(datesArr) };
        } else {
          datesArr.forEach((d) => byName[name].datesSet.add(d));
        }
      });
      return Object.values(byName).map((r) => ({
        reason_id: r.reason_id,
        reason_name: r.reason_name,
        dates: Array.from(r.datesSet).sort().join(', ')
      }));
    },

    buildReasonsFromMatrix() {
      if (!this.matrix || !this.dates || this.dates.length === 0) return;
      const reasonDatesMap = {};
      const formatToDDMM = (isoDate) => {
        if (!isoDate || isoDate.length < 10) return null;
        const parts = isoDate.slice(0, 10).split('-');
        if (parts.length !== 3) return null;
        return `${parts[2].padStart(2, '0')}.${parts[1].padStart(2, '0')}`;
      };
      this.matrix.forEach((row) => {
        if (row.is_labor) return;
        this.dates.forEach((dateInfo) => {
          const cell = row[dateInfo.colKey];
          if (cell?.reason_id && cell?.reason_name && dateInfo.isoDate) {
            const name = (cell.reason_name || '').trim();
            if (!name) return;
            if (!reasonDatesMap[name]) {
              reasonDatesMap[name] = { reason_id: cell.reason_id, reason_name: name, datesSet: new Set() };
            }
            reasonDatesMap[name].datesSet.add(dateInfo.isoDate.slice(0, 10));
          }
        });
      });
      const fromMatrix = Object.values(reasonDatesMap).map((r) => ({
        reason_id: r.reason_id,
        reason_name: r.reason_name,
        dates: Array.from(r.datesSet).sort().map(formatToDDMM).filter(Boolean).join(', ')
      }));
      if (fromMatrix.length > 0) {
        const matrixByName = {};
        fromMatrix.forEach((r) => {
          matrixByName[r.reason_name] = r.dates;
        });
        this.reasonsData = this.reasonsData.map((r) => {
          const matrixDates = matrixByName[r.reason_name];
          return {
            ...r,
            dates: (matrixDates && matrixDates.trim()) ? matrixDates : r.dates
          };
        });
        const existingNames = new Set(this.reasonsData.map((r) => r.reason_name));
        const onlyInMatrix = fromMatrix.filter((fm) => !existingNames.has(fm.reason_name));
        if (onlyInMatrix.length > 0) {
          this.reasonsData = [...this.reasonsData, ...onlyInMatrix];
        }
        this.reasonsData = this.deduplicateReasonsByName(this.reasonsData);
      }
    },
    
    normalizeReasonsData(rawList) {
      // Приводим к единому формату: reason_id, reason_name, dates (строка "08.11, 09.11")
      // API/БД могут вернуть даты в полях dates, dates_str или массивом
      return (rawList || []).map((r) => {
        let datesStr = '';
        if (typeof r.dates === 'string' && r.dates.trim()) {
          datesStr = r.dates.trim();
        } else if (typeof r.dates_str === 'string' && r.dates_str.trim()) {
          datesStr = r.dates_str.trim();
        } else if (Array.isArray(r.dates) && r.dates.length > 0) {
          datesStr = r.dates.map((d) => (typeof d === 'string' ? d : (d && d.date ? String(d.date) : ''))).filter(Boolean).join(', ');
        } else if (Array.isArray(r.date_list) && r.date_list.length > 0) {
          datesStr = r.date_list.map((d) => (typeof d === 'string' ? d : (d && d.date ? String(d.date) : ''))).filter(Boolean).join(', ');
        } else {
          // На случай другого имени поля (например из БД)
          const key = Object.keys(r || {}).find((k) => k.toLowerCase() === 'dates');
          if (key) {
            const v = r[key];
            if (typeof v === 'string' && v.trim()) datesStr = v.trim();
            else if (Array.isArray(v) && v.length > 0) datesStr = v.map((d) => (typeof d === 'string' ? d : String(d))).filter(Boolean).join(', ');
          }
        }
        return {
          reason_id: r.reason_id,
          reason_name: r.reason_name || r.reason_name_,
          dates: datesStr,
        };
      });
    },

    formatDateToDDMM(dateStr) {
      if (!dateStr || !dateStr.trim()) return '';
      const s = dateStr.trim();
      const m = s.match(/^(\d{1,2})[./](\d{1,2})(?:[./]\d{4})?$/);
      if (m) return `${m[1].padStart(2, '0')}.${m[2].padStart(2, '0')}`;
      return s;
    },
    getReasonDateForColumn(reason, columnNumber) {
      // reason.dates — строка вида "08/11, 09/11" или "08.11.2025, 09.11.2025" (после normalizeReasonsData)
      const datesStr = reason.dates;
      if (!datesStr || !datesStr.trim()) {
        return '';
      }
      
      const datesArray = datesStr.split(',').map((d) => this.formatDateToDDMM(d.trim())).filter((d) => d);
      
      // Возвращаем дату для указанной колонки (колонки нумеруются с 1)
      // columnNumber - это номер колонки (1-20)
      const dateIndex = columnNumber - 1; // Преобразуем в индекс массива (0-19)
      
      if (dateIndex >= 0 && dateIndex < datesArray.length) {
        return datesArray[dateIndex];
      }
      
      return '';
    },
    
    distributeFloorsByDates() {
      this.floorDistribution = {};
      
      if (!this.dates || this.dates.length === 0) {
        return;
      }
      
      // Берём данные напрямую из dateInfo (из calc_level_tab)
      this.dates.forEach((dateInfo, dateIndex) => {
        let levelNum = dateInfo.level_num;
        if (levelNum && typeof levelNum === 'string') {
          const match = levelNum.match(/^(\d+)/);
          if (match) {
            levelNum = parseInt(match[1]);
          }
        }

        this.floorDistribution[dateIndex] = {
          level_num: levelNum,
          floor_order: dateInfo.floor_order,
          day_no: dateInfo.day_no,
          is_extra_day: dateInfo.is_extra_day,
          behind_days: dateInfo.behind_days,
          ahead_days: dateInfo.ahead_days,
          shift_days: dateInfo.shift_days,
          is_frozen: dateInfo.is_frozen,
        };
      });
    },
    
    normalizeDateForComparison(dateStr) {
      if (!dateStr) return null;
      
      try {
        // Если уже в формате YYYY-MM-DD, возвращаем как есть
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }
        
        let date;
        if (typeof dateStr === 'string' && dateStr.includes('T')) {
          date = new Date(dateStr);
        } else if (typeof dateStr === 'string' && dateStr.includes('.')) {
          const parts = dateStr.split('.');
          if (parts.length === 3) {
            // DD.MM.YYYY -> создаём дату локально
            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
          }
        } else if (typeof dateStr === 'string' && dateStr.includes('-')) {
          // YYYY-MM-DD - возвращаем как есть (уже нормализовано выше, но на всякий случай)
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
          }
        }
        
        if (date && !isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      } catch (e) {
        console.error('Error normalizing date:', dateStr, e);
      }
      
      return null;
    },
    
    addFloorsRow() {
      this.matrix = this.matrix.filter(row => row.task_name !== 'Этажность');
      
      // Определяем, есть ли непогашенный долг по каждой дате (колонке)
      const hasDebtByColKey = {};
      this.dates.forEach((dateInfo) => {
        hasDebtByColKey[dateInfo.colKey] = false;
      });

      // Проходим по строкам задач (игнорируем "Выход людей" и уже добавленную ранее "Этажность")
      this.matrix.forEach((row) => {
        if (row.is_labor || row.task_name === 'Этажность') {
          return;
        }

        this.dates.forEach((dateInfo) => {
          const cellData = row[dateInfo.colKey];
          if (!cellData) {
            return;
          }

          const debtOutRaw = cellData.debt_out_value;
          if (
            debtOutRaw !== null &&
            debtOutRaw !== undefined &&
            String(debtOutRaw).trim() !== '' &&
            Number(debtOutRaw) > 0
          ) {
            hasDebtByColKey[dateInfo.colKey] = true;
          }
        });
      });

      const floorsRow = {
        task_name: 'Этажность',
        task_dict_id: -1,
        unit_name: '-',
      };
      
      this.dates.forEach((dateInfo, dateIndex) => {
        const floorData = this.floorDistribution[dateIndex];
        // shift_days — это сдвиг графика (ГПР), он не должен сбрасываться в 0
        // в день закрытия долга: иначе пропадает факт задержки (например 1(-3)).
        const shiftDays = floorData ? floorData.shift_days : null;
        // Сохраняем объект с данными для отображения и tooltip
        floorsRow[dateInfo.colKey] = floorData ? {
          level_num: floorData.level_num,
          level_display: floorData.level_display,
          day_cnt: floorData.day_cnt,
          calc_comment: floorData.calc_comment,
          behind_days: floorData.behind_days,
          ahead_days: floorData.ahead_days,
          shift_days: shiftDays,
          is_frozen: floorData.is_frozen,
        } : null;
      });
      
      this.matrix.push(floorsRow);
    },
    
    generateColumns() {
      const cols = [
        { name: 'task_name', label: 'Задача', field: 'task_name', align: 'left' }
      ];
      
      this.dates.forEach(dateInfo => {
        cols.push({
          name: dateInfo.colKey,
          label: dateInfo.displayDate,
          field: dateInfo.colKey,
          align: 'center'
        });
      });
      
      cols.push({ name: 'total_plan', label: 'План', field: 'total_plan', align: 'center' });
      cols.push({ name: 'total_fact', label: 'Факт', field: 'total_fact', align: 'center' });
      
      return cols;
    },
    
    getDateGroups() {
      return [{ dates: this.dates }];
    },
    
    shouldShowTotalsAfterGroup(groupIdx) {
      return groupIdx === this.getDateGroups().length - 1;
    },
    
    isWeekend(dateStr) {
      const date = new Date(dateStr);
      const day = date.getDay();
      return day === 0 || day === 6;
    },
    
    formatDateShort(dateStr) {
      const date = new Date(dateStr);
      const day = date.getDate();
      const monthNames = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
      const month = monthNames[date.getMonth()];
      return `${day} ${month}`;
    },
    
    getWeekday(dateStr) {
      const date = new Date(dateStr);
      const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
      return days[date.getDay()];
    },
    
    getFactCellClass(factValue, planValue) {
      if (factValue === null || factValue === undefined || factValue === '') {
        return '';
      }
      if (planValue === null || planValue === undefined || planValue === '' || planValue === 0) {
        return '';
      }
      
      const fact = parseFloat(factValue) || 0;
      const plan = parseFloat(planValue) || 0;
      
      if (plan === 0) return '';
      
      if (fact >= plan) {
        return 'fact-cell-green';
      } else {
        return 'fact-cell-red';
      }
    },
    
    getPlanCloseClass(cellData) {
      if (!cellData) return '';
      
      const planValue = parseFloat(cellData.plan_value) || 0;
      const planClosedValue = parseFloat(cellData.plan_closed_value) || 0;
      
      // Если плана нет, не красим
      if (planValue === 0) return '';
      
      // План полностью закрыт - зеленый, иначе красный
      if (planClosedValue >= planValue) {
        return 'fact-cell-green';
      } else {
        return 'fact-cell-red';
      }
    },
    
    getDebtCloseClass(cellData) {
      if (!cellData) return '';
      
      const debtInValue = parseFloat(cellData.debt_in_value) || 0;
      const debtClosedValue = parseFloat(cellData.debt_closed_value) || 0;
      
      // Если долга входящего нет, не красим
      if (debtInValue === 0) return '';
      
      // Долг полностью закрыт - зеленый, иначе красный
      if (debtClosedValue >= debtInValue) {
        return 'debt-close-green';
      } else {
        return 'debt-close-red';
      }
    },
    
    getLaborFactCellClass(planPeople, factPeople) {
      if (planPeople === null || planPeople === undefined || planPeople === '' ||
          factPeople === null || factPeople === undefined || factPeople === '') {
        return '';
      }

      const plan = parseFloat(planPeople);
      const fact = parseFloat(factPeople);

      if (!plan || isNaN(plan) || isNaN(fact)) {
        return '';
      }

      const percent = (fact / plan) * 100;

      // 90-100% — зелёный
      if (percent >= 90) {
        return 'labor-fact-green';
      }

      // 70-80% — жёлтый
      if (percent >= 70 && percent <= 80) {
        return 'labor-fact-yellow';
      }

      // Меньше 80% — красный
      if (percent < 80) {
        return 'labor-fact-red';
      }

      return '';
    },
    
    getFloorCellStyle(floorData, dateInfo = null) {
      // Визуальные приоритеты: collapsed (серый) > extra day (оранжевый) > обычная раскраска этажей
      if (this.isCollapsedColumn(dateInfo)) {
        return { backgroundColor: '#eeeeee', color: '#616161' };
      }
      if (dateInfo?.is_extra_day) {
        return { backgroundColor: '#ffe0b2', color: '#e65100' };
      }

      if (floorData === null || floorData === undefined) {
        return { backgroundColor: '#fff', color: '#000' };
      }
      
      // Поддержка и старого формата (число/строка) и нового (объект)
      let floorNum = floorData;
      if (typeof floorData === 'object' && floorData !== null) {
        floorNum = floorData.level_num;
      }
      
      if (floorNum === null || floorNum === undefined || floorNum === '') {
        return { backgroundColor: '#fff', color: '#000' };
      }
      
      // Извлекаем число из строки (например "1" или "1 этаж")
      let num = 0;
      if (typeof floorNum === 'number') {
        num = floorNum;
      } else if (typeof floorNum === 'string') {
        const match = floorNum.match(/\d+/);
        num = match ? parseInt(match[0]) : 0;
      }
      
      if (num <= 0) {
        return { backgroundColor: '#fff', color: '#000' };
      }
      
      const colors = [
        { bg: '#ffcdd2', color: '#b71c1c' },
        { bg: '#f8bbd9', color: '#880e4f' },
        { bg: '#e1bee7', color: '#4a148c' },
        { bg: '#d1c4e9', color: '#311b92' },
        { bg: '#c5cae9', color: '#1a237e' },
        { bg: '#bbdefb', color: '#0d47a1' },
        { bg: '#b3e5fc', color: '#01579b' },
        { bg: '#b2ebf2', color: '#006064' },
        { bg: '#b2dfdb', color: '#004d40' },
        { bg: '#c8e6c9', color: '#1b5e20' },
        { bg: '#dcedc8', color: '#33691e' },
        { bg: '#f0f4c3', color: '#827717' },
        { bg: '#fff9c4', color: '#f57f17' },
        { bg: '#ffecb3', color: '#ff6f00' },
        { bg: '#ffe0b2', color: '#e65100' },
        { bg: '#ffccbc', color: '#bf360c' },
      ];
      
      const colorIndex = (num - 1) % colors.length;
      
      return {
        backgroundColor: colors[colorIndex].bg,
        color: colors[colorIndex].color
      };
    },
    
    getFloorDisplay(floorData) {
      if (floorData === null || floorData === undefined) {
        return '';
      }
      
      // Поддержка и старого формата (число) и нового (объект)
      if (typeof floorData === 'object') {
        const levelNum = floorData.level_display || floorData.level_num || '';
        
        // Если is_frozen = true (будущий день после горизонта расчёта) - показываем только этаж
        if (floorData.is_frozen) {
          return levelNum;
        }
        
        const shift = floorData.shift_days || 0;
        
        // Формируем строку: +n опережение, -n отставание, 0 нет сдвига
        let shiftStr;
        if (shift > 0) {
          shiftStr = `+${shift}`;
        } else if (shift < 0) {
          shiftStr = `${shift}`;
        } else {
          shiftStr = '0';
        }
        
        return `${levelNum} (${shiftStr})`;
      }
      
      return floorData;
    },
    
    calculateRowPlanTotal(row, dates) {
      if (row.is_labor || row.task_name === 'Этажность') return '-';
      
      let total = 0;
      let hasData = false;
      
      dates.forEach(dateInfo => {
        const cellData = row[dateInfo.colKey];
        if (cellData && cellData.plan_value !== null && cellData.plan_value !== undefined && cellData.plan_value !== '') {
          total += parseFloat(cellData.plan_value) || 0;
          hasData = true;
        }
      });
      
      return hasData ? (Number.isInteger(total) ? total : total.toFixed(2)) : '-';
    },
    
    calculateRowFactTotal(row, dates) {
      if (row.is_labor || row.task_name === 'Этажность') return '-';
      
      let total = 0;
      let hasData = false;
      
      dates.forEach(dateInfo => {
        const cellData = row[dateInfo.colKey];
        if (cellData && cellData.fact_value !== null && cellData.fact_value !== undefined && cellData.fact_value !== '') {
          total += parseFloat(cellData.fact_value) || 0;
          hasData = true;
        }
      });
      
      return hasData ? (Number.isInteger(total) ? total : total.toFixed(2)) : '-';
    },
    
    // === Методы для редактирования ===
    
    // Получение work_group_level_id и fact_date для указанной даты
    getLevelInfoForDate(date) {
      const normalizedDate = this.normalizeDateForComparison(date);
      if (normalizedDate && Array.isArray(this.dates) && this.dates.length > 0) {
        const dateInfo = this.dates.find(d => d.isoDate === normalizedDate);
        if (dateInfo?.work_group_level_id) {
          return {
            work_group_level_id: dateInfo.work_group_level_id,
            fact_date: dateInfo.fact_date || normalizedDate
          };
        }
      }
      return null;
    },
    
    handleCellClick(row, dateInfo) {
      // Не кликаем по строке этажности
      if (row.task_name === 'Этажность') return;

      // Схлопнутый день не редактируем (на эту календарную дату факт учитывается только в первой колонке)
      if (this.isCollapsedColumn(dateInfo)) {
        this.showErrorSnackbar('Этот день схлопнут и не редактируется');
        return;
      }

      const colKey = dateInfo?.colKey;
      const isoDate = dateInfo?.isoDate;
      const levelId = dateInfo?.work_group_level_id;
      const factDate = dateInfo?.fact_date || isoDate;
      if (!colKey || !isoDate) return;

      const cellData = row[colKey] || {};
      
      // Не кликаем по автозакрытым ячейкам
      if (cellData.is_auto_closed) {
        this.showErrorSnackbar('Эта ячейка закрыта автоматически и не может быть отредактирована');
        return;
      }
      
      const selectedBlock = this.blocks.find(b => b.block_id === this.filters.block_id);
      const selectedWorkGroup = this.workGroups.find(wg => wg.work_group_id === this.filters.work_group_id);
      
      // Если это строка "Выход людей", открываем специальную модальную форму
      if (row.is_labor) {
        this.showModal(MODAL_NAMES_AIDAR.EditLaborFact, {
          title: 'Выход людей - Факт',
          block_name: selectedBlock?.block_name || '',
          work_group_name: selectedWorkGroup?.work_group_name || '',
          work_group_id: this.filters.work_group_id,
          task_name: row.task_name,
          labor_date: isoDate,
          labor_plan_fact_id: cellData?.labor_plan_fact_id || null,
          plan_people: cellData?.plan_people,
          fact_people: cellData?.fact_people,
          onSubmit: (formData) => this.onSubmitLaborFact(formData),
        });
      } else {
        // Обычная задача - берём work_group_level_id и fact_date из колонки
        this.showModal(MODAL_NAMES_AIDAR.EditTierFactTimeline, {
          title: 'Timeline План-Факт (Первый)',
          block_name: selectedBlock?.block_name || '',
          work_group_name: selectedWorkGroup?.work_group_name || '',
          work_group_id: this.filters.work_group_id,
          task_name: row.task_name,
          task_dict_id: row.task_dict_id,
          fact_date: factDate,
          plan_orig_value: cellData?.plan_value,
          plan_value: cellData?.plan_value,
          fact_orig_value: cellData?.fact_value,
          fact_value: cellData?.fact_value,
          // Передаём расчётные поля из calc_tab, чтобы корректно определять "долг"
          // при опережении (reserve_in_value) и долгах (debt_in_value / debt_out_value).
          debt_in_value: cellData?.debt_in_value,
          debt_out_value: cellData?.debt_out_value,
          reserve_in_value: cellData?.reserve_in_value,
          work_task_fact_id: cellData?.work_task_fact_id || null,
          reason_id: cellData?.reason_id || null,
          work_group_level_id: levelId,
          onSubmit: (formData) => this.onSubmitFact({ ...formData, work_group_level_id: levelId, fact_date: factDate }),
          onDelete: (deleteData) => this.onDeleteFact(deleteData),
        });
      }
    },
    
    async onSubmitFact(formData) {
      try {
        // Проверяем что этаж и дата определены
        if (!formData.work_group_level_id || !formData.fact_date) {
          this.showErrorSnackbar('Не удалось определить этаж или дату. Возможно, этаж не настроен для этой даты.');
          return;
        }
        
        // Вызываем endpoint для сохранения факта
        await saveFact({
          work_task_fact_id: formData.work_task_fact_id || null,
          work_group_id: formData.work_group_id,
          task_dict_id: formData.task_dict_id,
          fact_value: formData.fact_orig_value,
          work_group_level_id: formData.work_group_level_id,
          fact_date: formData.fact_date,
          reason_id: formData.reason_id || null,
        });
        
        this.showSucceedSnackbar('Факт успешно сохранен');
        this.hideModal();
        // Перезагружаем данные
        await this.reloadDataSilently();
      } catch (error) {
        console.error('Error saving fact:', error);
        // Извлекаем сообщение об ошибке
        let errorMessage = 'Ошибка сохранения факта';
        if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        this.showErrorSnackbar(errorMessage);
      }
    },
    
    async onSubmitLaborFact(formData) {
      try {
        await saveLaborFact({
          labor_plan_fact_id: formData.labor_plan_fact_id || null,
          work_group_id: formData.work_group_id,
          labor_date: formData.labor_date,
          fact_people: formData.fact_people || null,
        });
        
        this.showSucceedSnackbar('Факт людей успешно сохранен');
        this.hideModal();
        // Перезагружаем данные
        await this.reloadDataSilently();
      } catch (error) {
        console.error('Error saving labor fact:', error);
        this.showErrorSnackbar(error?.response?.data?.error || error?.message || 'Ошибка сохранения');
      }
    },
    
    async onDeleteFact(deleteData) {
      try {
        await deleteFact({
          work_task_fact_id: deleteData.work_task_fact_id,
        });
        
        this.showSucceedSnackbar('Факт успешно удален');
        this.hideModal();
        // Перезагружаем данные
        await this.reloadDataSilently();
      } catch (error) {
        console.error('Error deleting fact:', error);
        this.showErrorSnackbar(error?.response?.data?.error || error?.message || 'Ошибка удаления');
      }
    },
    
    async reloadDataSilently() {
      if (!this.filters.work_group_id) return;
      
      try {
        const result = await getTimelineData(
          this.filters.work_group_id,
          this.currentHalfMonth.year,
          this.currentHalfMonth.month,
          this.currentHalfMonth.half
        );
        this.tasks = result.tasks;
        this.matrix = result.matrix;
        this.dates = result.dates;
        
        let date_start = null, date_end = null;
        if (this.dates.length > 0) {
          const halfRange = getHalfMonthRangeISO(
            this.currentHalfMonth.year,
            this.currentHalfMonth.month,
            this.currentHalfMonth.half
          );
          date_start = halfRange.date_start;
          date_end = halfRange.date_end;
          const isoDates = this.dates.map((d) => d.isoDate).filter(Boolean);
          if (isoDates.length > 0) {
            const minDate = isoDates.reduce((a, b) => (a < b ? a : b));
            const maxDate = isoDates.reduce((a, b) => (a > b ? a : b));
            if (minDate < date_start) date_start = minDate;
            if (maxDate > date_end) date_end = maxDate;
          }
        }
        await this.loadReasons(this.filters.work_group_id, date_start, date_end);
        this.buildReasonsFromMatrix();
        
        this.columns = this.generateColumns();
        
        if (this.dates.length > 0) {
          this.distributeFloorsByDates();
          this.addFloorsRow();
        }
      } catch (e) {
        console.error('Error reloading data:', e);
      }
    },
    
    async refreshTableData() {
      if (!this.filters.work_group_id || this.isRefreshing) return;
      
      this.isRefreshing = true;
      try {
        await this.reloadDataSilently();
        this.showSucceedSnackbar('Данные обновлены');
      } catch (e) {
        console.error('Error refreshing data:', e);
        this.showErrorSnackbar('Ошибка обновления данных');
      } finally {
        this.isRefreshing = false;
      }
    },
    
    async recalcData() {
      if (!this.filters.work_group_id || this.isRecalcing) return;
      
      this.isRecalcing = true;
      try {
        const { tierFactFirstAPI } = await import('./api');
        await tierFactFirstAPI.recalcAll(this.filters.work_group_id);
        await this.reloadDataSilently();
        this.showSucceedSnackbar('Пересчёт завершён');
      } catch (e) {
        console.error('Error recalcing data:', e);
        this.showErrorSnackbar('Ошибка пересчёта');
      } finally {
        this.isRecalcing = false;
      }
    },
  },
  async mounted() {
    await this.loadResidents();
    await this.loadFiltersFromURL();
  },
};
</script>

<style scoped lang="scss">
.tier-fact-first-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.filters-content {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.table-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #999;
  font-size: 16px;
}

.table-header-with-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  position: relative;
  
  .table-title-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 200px;
  }
  
  .table-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-align: center;
  }

  .table-period {
    font-size: 13px;
    color: #666;
    font-weight: 500;
    text-align: center;
  }
  
  .contractor-info {
    font-size: 15px;
    color: #1976d2;
    font-weight: 600;
    text-align: center;
    padding: 6px 12px;
    background: #e3f2fd;
    border-radius: 4px;
    border: 1px solid #90caf9;
    margin-top: 4px;
  }
  
  .nav-btn {
    color: #1976d2;
    
    &:hover:not(:disabled) {
      background: rgba(25, 118, 210, 0.1);
    }
  }
  
  .tier-2-6-btn {
    margin-left: 16px;
  }
  
  .refresh-btn {
    margin-left: 8px;
    color: #1976d2;
    
    &:hover:not(:disabled) {
      background: rgba(25, 118, 210, 0.1);
    }
  }
  
  .details-btn {
    color: #9e9e9e;
    margin-left: 16px;
    
    &:hover:not(:disabled) {
      background: rgba(158, 158, 158, 0.1);
    }
    
    &.details-btn-active {
      color: #ff9800;
      background: rgba(255, 152, 0, 0.1);
    }
  }
}

.table-wrapper {
  flex: 1;
  overflow: auto;
}

.tier-fact-first-table {
  :deep(.q-table) {
    background: #fff;
    border-collapse: collapse;
    border: 1px solid #d0d0d0;
  }

  :deep(.q-table__middle.scroll) {
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      height: 14px;
    }

    &::-webkit-scrollbar-track {
      background: #f5f5f5;
    }

    &::-webkit-scrollbar-thumb {
      background: #b0b0b0;
      border-radius: 8px;
    }
  }

  :deep(.q-table th),
  :deep(.q-table td) {
    border: 1px solid #d0d0d0;
    padding: 0;
  }

  :deep(thead) {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f2f2f2;
  }

  .sticky-column {
    position: sticky;
    left: 0;
    z-index: 3;
    background: #f2f2f2;
    box-shadow: 2px 0 2px rgba(0, 0, 0, 0.1);
  }

  .task-name-header {
    min-width: 200px;
    max-width: 250px;
    font-weight: 600;
    background: #f2f2f2 !important;
    border: 1px solid #d0d0d0 !important;
    padding: 6px 8px !important;
    text-align: left;
    font-size: 12px;
    color: #000;
  }

  .task-name-cell {
    min-width: 200px;
    max-width: 250px;
    padding: 6px 8px;
    background: #fff;
    border: 1px solid #d0d0d0;
  }

  .task-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .task-name {
    font-weight: 500;
    color: #000;
    font-size: 14px;
    margin-bottom: 4px;
    
    .task-id-debug {
      font-size: 10px;
      color: #999;
      font-weight: 400;
    }
  }

  .unit-name {
    font-size: 11px;
    font-weight: 600;
    color: #1976d2;
    margin-top: 2px;
  }

  .date-header {
    min-width: 80px;
    max-width: 80px;
    width: 80px;
    height: 60px;
    padding: 4px 6px;
    text-align: center;
    background: #f2f2f2 !important;
    border: 1px solid #d0d0d0 !important;
    
    &.today-header {
      background: #dae8fc !important;
      font-weight: 600;
    }

    &.weekend-header {
      background: #ffebee !important;
    }

    &.today-header.weekend-header {
      background: #ffc1c1 !important;
    }

    &.collapsed-column {
      background: #d9d9d9 !important;
      color: #212121 !important;
      opacity: 1;
      box-shadow: inset 0 0 0 2px #757575;
      border-left: 3px solid #616161 !important;
      border-right: 3px solid #616161 !important;
    }

    &.extra-day-column {
      background: #ffcc80 !important;
      color: #bf360c !important;
      opacity: 1;
      box-shadow: inset 0 0 0 2px #fb8c00;
      border-left: 3px solid #fb8c00 !important;
      border-right: 3px solid #fb8c00 !important;
    }
  }

  .date-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .day-number {
    font-size: 13px;
    font-weight: 600;
    color: #000;
  }

  .day-weekday {
    font-size: 11px;
    font-weight: 600;
    color: #666;
  }

  .day-number.date-changed {
    color: #e65100;
  }

  .date-original {
    font-size: 9px;
    color: #999;
    margin-top: 1px;
  }

  .weekend-header .day-weekday {
    color: #c62828;
    font-weight: 700;
  }

  .plan-fact-header {
    font-size: 11px;
    font-weight: 700;
    padding: 4px 2px !important;
    background-color: #f2f2f2 !important;
    color: #000000 !important;
    border: 1px solid #d0d0d0 !important;
    line-height: 1.2;
    height: auto;
    min-width: 80px;
    max-width: 80px;
    width: 80px;
    text-align: center;

    &.collapsed-column {
      background: #d9d9d9 !important;
      color: #212121 !important;
      box-shadow: inset 0 0 0 2px #757575;
      border-left: 3px solid #616161 !important;
      border-right: 3px solid #616161 !important;
    }

    &.extra-day-column {
      background: #ffcc80 !important;
      color: #bf360c !important;
      box-shadow: inset 0 0 0 2px #fb8c00;
      border-left: 3px solid #fb8c00 !important;
      border-right: 3px solid #fb8c00 !important;
    }
  }

  .plan-fact-header-content {
    display: flex;
    justify-content: space-around;
    align-items: center;
    width: 100%;
  }

  .plan-label {
    flex: 1;
    text-align: center;
  }

  .fact-label {
    flex: 1;
    text-align: center;
    border-left: 1px solid #d0d0d0;
  }

  .floors-row {
    background: #e3f2fd !important;
  }
  
  .floors-task-cell {
    background: #e3f2fd !important;
    border: 1px solid #90caf9 !important;
  }
  
  .floors-task-name {
    font-weight: 700;
  }

  .floor-cell {
    padding: 8px;
    text-align: center;
    font-weight: 700;
    font-size: 16px;
    cursor: default;
    border: 1px solid;
    position: relative;
    color: #1565c0;
    
    &.today-cell {
      border-width: 2px;
    }

    &.collapsed-column {
      background: #d9d9d9 !important;
      border-color: #757575 !important;
      color: #212121 !important;
      box-shadow: inset 0 0 0 2px #757575;
      border-left: 3px solid #616161 !important;
      border-right: 3px solid #616161 !important;
    }

    &.extra-day-column {
      background: #ffcc80 !important;
      border-color: #fb8c00 !important;
      color: #bf360c !important;
      box-shadow: inset 0 0 0 2px #fb8c00;
      border-left: 3px solid #fb8c00 !important;
      border-right: 3px solid #fb8c00 !important;
    }
  }

  .floor-cell-content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    font-weight: 700;
    font-size: 16px;
  }

  .data-cell {
    padding: 0;
    cursor: pointer;
    border: 1px solid #d0d0d0;
    background: #fff;
    min-width: 80px;
    max-width: 80px;
    width: 80px;
    height: 80px;
    
    &:hover {
      background-color: #e7f3ff !important;
    }

    &.weekend-cell {
      background-color: #fff5f5 !important;
    }
    
    &.today-cell {
      background-color: #dae8fc !important;
    }

    &.today-cell.weekend-cell {
      background-color: #ffe0e0 !important;
    }
    
    &.auto-closed-cell {
      background-color: #e0e0e0 !important;
      cursor: not-allowed;
      opacity: 0.7;
      
      &:hover {
        background-color: #d0d0d0 !important;
      }
      
      .cell-wrapper {
        position: relative;
        
        &::after {
          content: 'Авто';
          position: absolute;
          top: 2px;
          right: 2px;
          font-size: 8px;
          color: #666;
          background: rgba(255,255,255,0.8);
          padding: 1px 3px;
          border-radius: 2px;
        }
      }
    }

    &.collapsed-column {
      background-color: #d9d9d9 !important;
      cursor: not-allowed;
      opacity: 1;
      box-shadow: inset 0 0 0 2px #757575;
      border-left: 3px solid #616161 !important;
      border-right: 3px solid #616161 !important;

      &:hover {
        background-color: #d9d9d9 !important;
      }

      .fact-info-row {
        background: #f5f5f5 !important;
        border-bottom-color: #d0d0d0 !important;
      }
    }

    &.extra-day-column {
      background-color: #ffcc80 !important;
      box-shadow: inset 0 0 0 2px #fb8c00;
      border-left: 3px solid #fb8c00 !important;
      border-right: 3px solid #fb8c00 !important;

      &:hover {
        background-color: #ffcc80 !important;
      }
    }
  }

  .cell-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
  }
  
  .fact-info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2px 4px;
    background: #e3f2fd;
    border-bottom: 1px solid #90caf9;
    font-size: 10px;
    font-weight: 600;
    min-height: 18px;
    
    .fact-entered {
      display: flex;
      align-items: center;
      gap: 2px;
      color: #1565c0;
    }
    
    .reserve-value {
      display: flex;
      align-items: center;
      gap: 2px;
      color: #2e7d32;
      background: #e8f5e9;
      padding: 1px 4px;
      border-radius: 3px;
    }
  }

  .cell-grid-2x2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    width: 100%;
    flex: 1;
    gap: 0;
    border: none;
  }

  .grid-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 4px;
    margin: 0;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.2;
    box-sizing: border-box;
    color: #000;
    position: relative;
    
    &.top-left {
      border-right: 1px solid #d0d0d0;
      border-bottom: 1px solid #d0d0d0;
    }
    
    &.top-right {
      border-bottom: 1px solid #d0d0d0;
    }
    
    &.bottom-left {
      border-right: 1px solid #d0d0d0;
    }
  }

  .plan-cell {
    background-color: #e7e6e6;
    color: #000;
    
    &.plan-cell-empty {
      background-color: #f5f5f5;
    }
  }

  .fact-cell {
    background-color: #fff;
    color: #333;
    font-size: 12px;
    font-weight: 600;
    
    &.fact-cell-green {
      background-color: #c8e6c9;
      color: #1b5e20;
    }
    
    &.fact-cell-red {
      background-color: #ffcdd2;
      color: #b71c1c;
    }
  }

  .debt-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 500;
    background: #f5f5f5;
    color: #555;
    
    &.has-debt {
      background: #ffcdd2;
      color: #b71c1c;
      font-weight: 600;
    }
  }

  .debt-close-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 500;
    background: #f5f5f5;
    color: #555;
    
    &.debt-close-green {
      background: #c8e6c9;
      color: #1b5e20;
      font-weight: 600;
    }
    
    &.debt-close-red {
      background: #ffcdd2;
      color: #b71c1c;
      font-weight: 600;
    }
  }

  .total-header {
    min-width: 60px;
    max-width: 60px;
    width: 60px;
    padding: 4px 6px;
    text-align: center;
    background: #d4d4d4 !important;
    border: 2px solid #999 !important;
    font-weight: 700;
    font-size: 11px;
    color: #000;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      border-left: 3px solid #666;
      border-right: 3px solid #666;
    }
  }

  .total-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .total-label {
    font-size: 10px;
    font-weight: 600;
    color: #000;
    white-space: nowrap;
  }

  .total-cell {
    min-width: 60px;
    max-width: 60px;
    width: 60px;
    height: 80px;
    padding: 4px;
    text-align: center;
    background: #f9f9f9;
    border: 2px solid #999;
    vertical-align: middle;
    font-weight: 700;
    font-size: 13px;
    color: #000;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: #666;
    }
    
    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: #666;
    }
    
    &.total-plan-cell {
      background: #e0e0e0;
      border-left: 3px solid #666;
      border-right: 2px solid #999;
    }
    
    &.total-fact-cell {
      background: #f0f0f0;
      border-left: 2px solid #999;
      border-right: 3px solid #666;
    }
  }

  .total-cell-content {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .labor-row {
    background: #e8f5e9 !important;
    
    &:hover {
      background: #c8e6c9 !important;
    }
  }

  .contractor-info-cell {
    font-size: 13px;
    font-weight: 600;
    color: #1976d2;
    margin-top: 4px;
    background: #e3f2fd;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
  }

  // Жестко уменьшаем высоту всей строки "Выход людей"
  :deep(.q-table tbody tr.labor-row > td) {
    height: 40px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }

  .labor-task-cell {
    background: #e8f5e9 !important;
    border: 1px solid #81c784 !important;
  }

  .labor-task-name {
    font-weight: 700;
    color: #2e7d32;
    font-size: 12px;
    line-height: 1.2;
  }

  .labor-cell {
    padding: 0;
    text-align: center;
    background: #f9f9f9 !important;
    border: 1px solid #d0d0d0 !important;
    cursor: pointer;
    height: 100%;
    vertical-align: middle;
    
    &:hover {
      background: #e1f5fe !important;
      border-color: #0288d1 !important;
    }
    
    &.today-cell {
      background: #fff3e0 !important;
      border: 2px solid #ff6f00 !important;
    }
    
    &.has-data {
      background: #fff !important;
    }
  }

  .labor-cell-grid {
    display: flex;
    height: 100%;
    width: 100%;
  }

  .labor-grid-cell {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    padding: 2px;
    border-right: 1px solid #d0d0d0;
    
    &:last-child {
      border-right: none;
    }
  }

  .labor-plan-cell {
    background: #e3f2fd;
    color: #1976d2;
  }

  .labor-fact-cell {
    background: #e8f5e9;
    color: #388e3c;
  }

  .labor-fact-cell.labor-fact-green {
    background: #c8e6c9;
    color: #1b5e20;
  }

  .labor-fact-cell.labor-fact-yellow {
    background: #fff9c4;
    color: #f57f17;
  }

  .labor-fact-cell.labor-fact-red {
    background: #ffcdd2;
    color: #b71c1c;
  }

  // Таблица причин отставания
  .reasons-table-container {
    margin-top: 20px;
    border: 1px solid #d0d0d0;
    background: #fff;
  }

  .reasons-table-title {
    padding: 12px;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    background: #f5f5f5;
    border-bottom: 2px solid #d0d0d0;
  }

  .reasons-table-wrapper {
    overflow-x: auto;
    max-height: 400px;
    overflow-y: auto;
  }

  .reasons-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    background: #fff;

    th, td {
      border: 1px solid #d0d0d0;
      padding: 6px 4px;
      text-align: center;
      vertical-align: middle;
    }

    .reasons-table-header-number {
      min-width: 40px;
      max-width: 40px;
      width: 40px;
      background: #fff3cd;
    }

    .reasons-table-header-reason {
      min-width: 250px;
      background: #fff3cd;
      text-align: left;
      padding-left: 8px;
    }

    .reasons-table-header-date {
      min-width: 50px;
      max-width: 50px;
      width: 50px;
      background: #fff3cd;
    }

    .reasons-table-cell-number {
      min-width: 40px;
      max-width: 40px;
      width: 40px;
      background: #f9f9f9;
      font-weight: 600;
    }

    .reasons-table-cell-reason {
      min-width: 250px;
      text-align: left;
      padding-left: 8px;
      background: #fff;
    }

    .reasons-table-cell-date {
      min-width: 50px;
      max-width: 50px;
      width: 50px;
      background: #fff;
      color: #d32f2f;
      font-weight: 500;
    }
  }
}
</style>
